/**
 * Turning the backend's timestamps into the parent's local time.
 *
 * THE BUG THIS EXISTS TO FIX
 * --------------------------
 * The API stamps everything with DateTime.UtcNow and serialises it WITHOUT a
 * timezone marker — "2026-08-26T17:07:21.8414372", no trailing Z, no +05:30.
 *
 * ECMAScript is explicit about what that means: a date-TIME string carrying no
 * offset is parsed as LOCAL time. So `new Date(thatString)` reads a UTC instant as
 * if it were already local, and every clock time rendered from it is wrong by the
 * device's UTC offset — 5 hours in Cayman, 5.5 the other way in India.
 *
 * Appending "Z" is what makes it parse as the UTC it actually is; only then does
 * toLocaleTimeString()/toLocaleDateString() convert it to the parent's own clock.
 *
 * THE SECOND, OPPOSITE TRAP
 * -------------------------
 * A DATE-ONLY string ("2026-08-30", no time part) is the reverse case: the spec
 * parses that form as UTC midnight. In a negative-offset zone like Cayman (UTC-5)
 * that renders as the PREVIOUS DAY. Such values are not instants at all — a due
 * date is the same calendar day everywhere — so they must never be shifted.
 * parseBackendDateOnly() pins them to local midnight instead.
 *
 * Use parseBackendDate for anything with a time of day; parseBackendDateOnly for
 * plain calendar dates. Picking the wrong one reintroduces one bug or the other.
 */

/**
 * Makes a backend timestamp unambiguous so Date parses it as UTC.
 *
 * Values that already carry an offset (+05:30, or a trailing Z) are left as the
 * instant they already denote.
 */
export function normalizeToUtcIso(dateString: string): string {
  const trimmed = dateString.trim();

  // Already offset-qualified: "…+05:30" / "…-05:00". The lastIndexOf check looks
  // past the date's own hyphens (positions 4 and 7) so "2026-08-26" isn't misread
  // as carrying a negative offset.
  if (trimmed.includes('+') || trimmed.lastIndexOf('-') > 10) {
    return new Date(trimmed).toISOString();
  }

  if (!trimmed.includes('Z')) {
    return `${trimmed}Z`;
  }

  return trimmed;
}

/** True when the string has no time component, e.g. "2026-08-30". */
function isDateOnly(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

/**
 * A backend timestamp as a Date positioned at the correct instant.
 *
 * Returns null for empty/unparseable input so callers can fall back rather than
 * render "Invalid Date". A date-only string is routed to local midnight, since
 * treating it as a UTC instant is the previous-day bug described above.
 */
export function parseBackendDate(value?: string | null): Date | null {
  if (!value) return null;

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const parsed = isDateOnly(trimmed)
    ? new Date(`${trimmed}T00:00:00`)
    : new Date(normalizeToUtcIso(trimmed));

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A plain calendar date (due dates, dates of birth, exam days) as LOCAL midnight.
 *
 * Deliberately ignores any time component: these values name a day, not an
 * instant, and must read as the same day in every timezone.
 */
export function parseBackendDateOnly(value?: string | null): Date | null {
  if (!value) return null;

  const datePart = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;

  const parsed = new Date(`${datePart}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Normalises a timestamp in place, for values stored on a model and parsed later
 * by many different screens. Fixing it once at the boundary beats making every
 * consumer remember to. Non-timestamps are returned untouched.
 */
export function toUtcIso(value?: string | null): string {
  if (!value) return '';

  const trimmed = String(value).trim();
  if (!trimmed || isDateOnly(trimmed)) return trimmed;

  // The normalized string is returned as-is rather than round-tripped through
  // Date#toISOString: an input already ending in Z is correct as it stands, and
  // reformatting it would rewrite "…08:20:00Z" as "…08:20:00.000Z" for no reason.
  const normalized = normalizeToUtcIso(trimmed);
  return Number.isNaN(new Date(normalized).getTime()) ? trimmed : normalized;
}

// ── Display helpers ─────────────────────────────────────────────────────────

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
};

/** "1:30 PM" in the device's own timezone. */
export function formatLocalTime(value?: string | null, fallback = ''): string {
  const parsed = parseBackendDate(value);
  return parsed ? parsed.toLocaleTimeString(undefined, TIME_OPTS) : fallback;
}

/** "26 Aug 2026" in the device's own timezone. */
export function formatLocalDate(value?: string | null, fallback = ''): string {
  const parsed = parseBackendDate(value);
  return parsed
    ? parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
    : fallback;
}

/**
 * "26 Aug 2026" for a plain calendar date (date of birth, due date, exam day).
 * Unlike formatLocalDate, never shifts the day for a date-only value — use this
 * one for dates of birth rather than formatLocalDate.
 */
export function formatLocalDateOnly(value?: string | null, fallback = ''): string {
  const parsed = parseBackendDateOnly(value);
  return parsed
    ? parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
    : fallback;
}

/** "26 Aug 2026, 1:30 PM" in the device's own timezone. */
export function formatLocalDateTime(value?: string | null, fallback = ''): string {
  const parsed = parseBackendDate(value);
  if (!parsed) return fallback;

  const date = parsed.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return `${date}, ${parsed.toLocaleTimeString(undefined, TIME_OPTS)}`;
}

/**
 * "just now" / "5m ago" / "3h ago" / "2d ago", else an absolute date.
 *
 * Replaces the hand-rolled diff arithmetic that several screens each had their own
 * copy of — all of which were computing the diff against a mis-parsed Date.
 */
export function formatRelativeTime(value?: string | null, fallback = ''): string {
  const parsed = parseBackendDate(value);
  if (!parsed) return fallback;

  const diffMs = Date.now() - parsed.getTime();

  // Clock skew, or a genuinely future timestamp: "in 2h" reads better than "-2h ago".
  if (diffMs < 0) return formatLocalDateTime(value, fallback);

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return formatLocalDate(value, fallback);
}
